import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Input from '../../../components/common/data-form/Input'
import Button from '../../../components/common/Button'
import Tag from '../../../components/common/Tag'
import useData from '../../../common/hooks/useData'
import useToast from '../../../common/hooks/useToast'
import fetchAPI from '../../../common/fetchAPI'
import Spinner from '../../../components/common/Spinner'
import ErrorMessage from '../../../components/common/ErrorMessage'
import SectionContainer from '../../../components/common/SectionContainer'
import useError from '../../../common/hooks/useError'
import useAuth from '../../../common/hooks/useAuth'

const subSectionOptions = [
    { value: 'all', label: 'All members' },
    { value: 'admins', label: 'Admins only' },
    { value: 'members', label: 'Members only' },
]

export default function ResourceOrgPermissions() {
    const { resource, id, permission } = useParams()
    const auth = useAuth()
    const [targets, setTargets] = useState([])
    const [orgNames, setOrgNames] = useState({})
    const [data, setData] = useState({
        organizationId: null,
        subSection: 'all',
    })
    const [searchKey, setSearchKey] = useState(0)
    const [actionLoading, setActionLoading] = useState(null)
    const [error, setError] = useError()
    const setToast = useToast()

    const { permissionsLoading, permissionsError } = useData(
        `/v1/settings/permissions/resources/${resource}/${id}?targetType=org&scope=${permission}`,
        'permissions',
        null,
        null,
        null,
        (items) =>
            setTargets(
                items.map((item) => {
                    const [organizationId, subSection] = item.target.split(':')
                    return { organizationId, subSection }
                }),
            ),
    )

    useEffect(() => {
        async function loadMissingOrgNames() {
            const missingIds = targets
                .map((t) => t.organizationId)
                .filter((orgId) => orgId && !orgNames[orgId])
            if (!missingIds.length) {
                return
            }
            const uniqueIds = [...new Set(missingIds)]
            const results = await Promise.all(
                uniqueIds.map((orgId) =>
                    fetchAPI(`/v1/organizations/${orgId}?fields=name`),
                ),
            )
            setOrgNames((prev) => {
                const next = { ...prev }
                uniqueIds.forEach((orgId, index) => {
                    next[orgId] = results[index]?.responseData?.name || orgId
                })
                return next
            })
        }
        loadMissingOrgNames()
    }, [targets])

    function getItemsFromResults(res) {
        const results = res?.results || []
        if (!auth.isClient) {
            return results
        }
        return results.filter((org) =>
            org.members?.some((m) => m.userId === auth.user.id),
        )
    }

    async function saveTarget(organizationId, subSection, value) {
        setActionLoading(`${organizationId}:${subSection}`)
        const { error } = await fetchAPI(
            `/v1/settings/permissions/resources/${resource}/${id}`,
            {
                scope: permission,
                target: `${organizationId}:${subSection}`,
                value,
                targetType: 'orgIdMembership',
            },
            'PATCH',
        )
        setActionLoading(null)
        if (error) {
            setError(error)
            return false
        }
        setToast('Saved')
        return true
    }

    async function addTarget() {
        if (!data.organizationId) {
            return
        }
        const exists = targets.some(
            (t) =>
                t.organizationId === data.organizationId &&
                t.subSection === data.subSection,
        )
        if (exists) {
            return
        }
        const saved = await saveTarget(
            data.organizationId,
            data.subSection,
            true,
        )
        if (!saved) {
            return
        }
        setTargets((prev) => [...prev, { ...data }])
        setData({ organizationId: null, subSection: data.subSection })
        setSearchKey((k) => k + 1)
    }

    async function removeTarget(organizationId, subSection) {
        const saved = await saveTarget(organizationId, subSection, false)
        if (!saved) {
            return
        }
        setTargets((prev) =>
            prev.filter(
                (t) =>
                    !(
                        t.organizationId === organizationId &&
                        t.subSection === subSection
                    ),
            ),
        )
    }

    if (permissionsLoading) {
        return <Spinner />
    }

    if (permissionsError) {
        return (
            <SectionContainer>
                <ErrorMessage>{permissionsError}</ErrorMessage>
            </SectionContainer>
        )
    }

    const orgSearchUrl = `/v1/organizations?fields=name${
        auth.isClient ? '&populate=members.user.email' : ''
    }&limit=20&page=1`

    return (
        <>
            {!!error && (
                <ErrorMessage section onDismiss={() => setError('')}>
                    {error}
                </ErrorMessage>
            )}

            <SectionContainer>
                <h2 className="h3">{`${resource
                    .replace('-', ' ')
                    .toUpperCase()} - ${id} - "${permission}"`}</h2>
            </SectionContainer>

            <SectionContainer>
                <Input
                    key={searchKey}
                    id="form-org-search"
                    type="liveSearch"
                    label="Organization"
                    url={orgSearchUrl}
                    getResultValue={(org) => org?.name || ''}
                    getItemsFromResults={getItemsFromResults}
                    onItemClick={(org) => {
                        setData({
                            ...data,
                            organizationId: org?.id || null,
                        })
                        if (org) {
                            setOrgNames((prev) => ({
                                ...prev,
                                [org.id]: org.name,
                            }))
                        }
                    }}
                />

                <Input
                    id="form-org-subsection"
                    type="select"
                    label="Role"
                    value={data.subSection}
                    onChange={(v) => setData({ ...data, subSection: v })}
                    options={subSectionOptions}
                />

                <Button
                    type="button"
                    text="Add"
                    small
                    disabled={!data.organizationId}
                    isLoading={
                        actionLoading ===
                        `${data.organizationId}:${data.subSection}`
                    }
                    onClick={addTarget}
                />
            </SectionContainer>

            {!!targets.length && (
                <SectionContainer card>
                    {targets.some((t) => !orgNames[t.organizationId]) ? (
                        <div>
                            <Spinner />
                        </div>
                    ) : (
                        <div>
                            {targets.map(({ organizationId, subSection }) => (
                                <Tag
                                    key={`${organizationId}:${subSection}`}
                                    color="primary"
                                    onClose={() =>
                                        removeTarget(organizationId, subSection)
                                    }
                                >
                                    {`${
                                        orgNames[organizationId] ||
                                        organizationId
                                    } (${
                                        subSectionOptions.find(
                                            (o) => o.value === subSection,
                                        )?.label
                                    })`}
                                </Tag>
                            ))}
                        </div>
                    )}
                </SectionContainer>
            )}
        </>
    )
}
